import type { UpgradeDefinition, UpgradeColumn } from "@/types/upgrade.types"

// ============================================================================
// COLUMN 1: MILITARY (Archer + Barracks)
// ============================================================================

const militaryUpgrades: UpgradeDefinition[] = [
  // ROOT
  {
    id: "military-root",
    name: "Basic Training",
    description: "Archer towers and Barracks units deal 5% more damage",
    icon: "/images/techtree/upgrades/Ability_icons1_75.png",
    column: "military",
    branch: null,
    tier: 0,
    cost: 50,
    type: "passive",
    maxPurchases: 5,
    effects: [
      { target: "archer", stat: "damage", value: 1.05 },
      { target: "soldiers", stat: "damage", value: 1.05 },
    ],
  },
  // BRANCH A: Archery
  {
    id: "military-a1",
    name: "Steady Aim",
    description: "Archer towers gain 10% increased range",
    icon: "/images/techtree/upgrades/Ability_icons1_03.png",
    column: "military",
    branch: "a",
    tier: 1,
    cost: 75,
    type: "passive",
    maxPurchases: 5,
    effects: [{ target: "archer", stat: "range", value: 1.1 }],
  },
  {
    id: "military-a2",
    name: "Rapid Fire",
    description: "Archer towers attack 15% faster",
    icon: "/images/techtree/upgrades/Ability_icons1_23.png",
    column: "military",
    branch: "a",
    tier: 2,
    cost: 100,
    type: "passive",
    maxPurchases: 5,
    effects: [{ target: "archer", stat: "attackSpeed", value: 1.15 }],
  },
  {
    id: "military-a3",
    name: "Piercing Arrows",
    description: "Activate: Next 10 archer shots pierce through enemies",
    icon: "/images/techtree/upgrades/Ability_icons1_27.png",
    column: "military",
    branch: "a",
    tier: 3,
    cost: 150,
    type: "active",
    maxPurchases: 1,
    effects: [{ target: "archer", ability: "piercing-arrows" }],
  },
  // BRANCH B: Infantry
  {
    id: "military-b1",
    name: "Reinforcements",
    description: "Barracks spawn +1 soldier. First purchase grants Medium armor.",
    icon: "/images/techtree/upgrades/Ability_icons1_69.png",
    column: "military",
    branch: "b",
    tier: 1,
    cost: 75,
    type: "passive",
    maxPurchases: 5,
    effects: [
      { target: "barracks", stat: "spawnCount", value: 1, isFlat: true },
      { target: "soldiers", stat: "defenseType", value: "medium", isFirstPurchaseOnly: true },
    ],
  },
  {
    id: "military-b2",
    name: "Hardened Veterans",
    description: "Soldiers have 25% more HP. First purchase grants Heavy armor.",
    icon: "/images/techtree/upgrades/Ability_icons1_75.png",
    column: "military",
    branch: "b",
    tier: 2,
    cost: 100,
    type: "passive",
    maxPurchases: 5,
    effects: [
      { target: "soldiers", stat: "hp", value: 1.25 },
      { target: "soldiers", stat: "defenseType", value: "heavy", isFirstPurchaseOnly: true },
    ],
  },
  {
    id: "military-b3",
    name: "Battle Cry",
    description: "Activate: All soldiers gain +50% damage and +50% armor for 10 seconds",
    icon: "/images/techtree/upgrades/Ability_icons1_100.png",
    column: "military",
    branch: "b",
    tier: 3,
    cost: 150,
    type: "active",
    maxPurchases: 1,
    effects: [{ target: "soldiers", ability: "battle-cry" }],
  },
]

// ============================================================================
// COLUMN 2: ARCANE (Mage + Siege)
// ============================================================================

const arcaneUpgrades: UpgradeDefinition[] = [
  // ROOT
  {
    id: "arcane-root",
    name: "Arcane Infusion",
    description: "Mage and Siege towers deal 5% more damage",
    icon: "/images/techtree/upgrades/Ability_icons10_53.png",
    column: "arcane",
    branch: null,
    tier: 0,
    cost: 50,
    type: "passive",
    maxPurchases: 5,
    effects: [
      { target: "mage", stat: "damage", value: 1.05 },
      { target: "siege", stat: "damage", value: 1.05 },
    ],
  },
  // BRANCH A: Sorcery
  {
    id: "arcane-a1",
    name: "Mana Surge",
    description: "Mage towers deal 10% more spell damage",
    icon: "/images/techtree/upgrades/Ability_icons10_49.png",
    column: "arcane",
    branch: "a",
    tier: 1,
    cost: 75,
    type: "passive",
    maxPurchases: 5,
    effects: [{ target: "mage", stat: "damage", value: 1.1 }],
  },
  {
    id: "arcane-a2",
    name: "Chain Lightning",
    description: "Mage attacks bounce to 1 additional enemy",
    icon: "/images/techtree/upgrades/Ability_icons10_20.png",
    column: "arcane",
    branch: "a",
    tier: 2,
    cost: 100,
    type: "passive",
    maxPurchases: 5,
    effects: [{ target: "mage", ability: "chain-lightning" }],
  },
  {
    id: "arcane-a3",
    name: "Meteor Strike",
    description: "Activate: Call down a meteor at target location",
    icon: "/images/techtree/upgrades/Ability_icons10_18.png",
    column: "arcane",
    branch: "a",
    tier: 3,
    cost: 150,
    type: "active",
    maxPurchases: 1,
    effects: [{ target: "mage", ability: "meteor-strike" }],
  },
  // BRANCH B: Artillery
  {
    id: "arcane-b1",
    name: "Heavy Ordnance",
    description: "Siege towers deal 15% more damage",
    icon: "/images/techtree/upgrades/Ability_icons10_24.png",
    column: "arcane",
    branch: "b",
    tier: 1,
    cost: 75,
    type: "passive",
    maxPurchases: 5,
    effects: [{ target: "siege", stat: "damage", value: 1.15 }],
  },
  {
    id: "arcane-b2",
    name: "Explosive Rounds",
    description: "Siege attacks deal area damage",
    icon: "/images/techtree/upgrades/Ability_icons10_41.png",
    column: "arcane",
    branch: "b",
    tier: 2,
    cost: 100,
    type: "passive",
    maxPurchases: 5,
    effects: [{ target: "siege", ability: "explosive-rounds" }],
  },
  {
    id: "arcane-b3",
    name: "Bombardment",
    description: "Activate: Siege towers fire rapidly for 5 seconds",
    icon: "/images/techtree/upgrades/Ability_icons10_28.png",
    column: "arcane",
    branch: "b",
    tier: 3,
    cost: 150,
    type: "active",
    maxPurchases: 1,
    effects: [{ target: "siege", ability: "bombardment" }],
  },
]

// ============================================================================
// COLUMN 3: COMMAND (Hero + Shop + Map)
// ============================================================================

const commandUpgrades: UpgradeDefinition[] = [
  // ROOT
  {
    id: "command-root",
    name: "Leadership",
    description: "Hero moves 10% faster",
    icon: "/images/techtree/upgrades/Ability_icons5_14.png",
    column: "command",
    branch: null,
    tier: 0,
    cost: 50,
    type: "passive",
    maxPurchases: 5,
    effects: [{ target: "hero", stat: "speed", value: 1.1 }],
  },
  // BRANCH A: Champion
  {
    id: "command-a1",
    name: "Quick Reflexes",
    description: "Skill cooldowns are reduced by 15%",
    icon: "/images/techtree/upgrades/Ability_icons5_15.png",
    column: "command",
    branch: "a",
    tier: 1,
    cost: 75,
    type: "passive",
    maxPurchases: 5,
    effects: [{ target: "hero", stat: "cooldown", value: 0.85 }],
  },
  {
    id: "command-a2",
    name: "Battle Hardened",
    description: "Hero has 20% more max HP",
    icon: "/images/techtree/upgrades/Ability_icons5_38.png",
    column: "command",
    branch: "a",
    tier: 2,
    cost: 100,
    type: "passive",
    maxPurchases: 5,
    effects: [{ target: "hero", stat: "hp", value: 1.2 }],
  },
  {
    id: "command-a3",
    name: "Heroic Surge",
    description: "Activate: Reset all skill cooldowns instantly",
    icon: "/images/techtree/upgrades/Ability_icons8_19.png",
    column: "command",
    branch: "a",
    tier: 3,
    cost: 150,
    type: "active",
    maxPurchases: 1,
    effects: [{ target: "hero", ability: "heroic-surge" }],
  },
  // BRANCH B: Commander
  {
    id: "command-b1",
    name: "Trade Routes",
    description: "Shop prices are reduced by 10%",
    icon: "/images/techtree/upgrades/Ability_icons5_40.png",
    column: "command",
    branch: "b",
    tier: 1,
    cost: 75,
    type: "passive",
    maxPurchases: 5,
    effects: [{ target: "shop", stat: "price", value: 0.1, isFlat: true }],
  },
  {
    id: "command-b2",
    name: "Cartography",
    description: "Reveal 2 extra nodes on the star map",
    icon: "/images/techtree/upgrades/Ability_icons5_80.png",
    column: "command",
    branch: "b",
    tier: 2,
    cost: 100,
    type: "passive",
    maxPurchases: 5,
    effects: [{ target: "map", stat: "revealNodes", value: 2, isFlat: true }],
  },
  {
    id: "command-b3",
    name: "Expedition",
    description: "Activate: Unlock special event nodes on the map",
    icon: "/images/techtree/upgrades/Ability_icons5_97.png",
    column: "command",
    branch: "b",
    tier: 3,
    cost: 150,
    type: "active",
    maxPurchases: 1,
    effects: [{ target: "map", ability: "expedition" }],
  },
]

// ============================================================================
// EXPORTS
// ============================================================================

export const allUpgrades: UpgradeDefinition[] = [
  ...militaryUpgrades,
  ...arcaneUpgrades,
  ...commandUpgrades,
]

export const upgradeDefinitions: Record<string, UpgradeDefinition> = Object.fromEntries(
  allUpgrades.map((u) => [u.id, u])
)

export function getUpgradesByColumn(column: UpgradeColumn): UpgradeDefinition[] {
  return allUpgrades.filter((u) => u.column === column)
}

export function getRootUpgrade(column: UpgradeColumn): UpgradeDefinition | undefined {
  return allUpgrades.find((u) => u.column === column && u.tier === 0)
}

export function getBranchUpgrades(
  column: UpgradeColumn,
  branch: "a" | "b"
): UpgradeDefinition[] {
  return allUpgrades
    .filter((u) => u.column === column && u.branch === branch)
    .sort((a, b) => a.tier - b.tier)
}

export const columnLabels: Record<UpgradeColumn, string> = {
  military: "Military",
  arcane: "Arcane",
  command: "Command",
}

export const columnDescriptions: Record<UpgradeColumn, string> = {
  military: "Archer & Barracks",
  arcane: "Mage & Siege",
  command: "Hero & Economy",
}
